/**
 * The app's side of the Site selection bridge. A Site page runs in a
 * sandboxed frame, with an opaque origin of its own although it is served
 * from the app's address, and can only *propose* what the person
 * selected there; the person then writes and saves the comment in Mystiq's
 * own interface. A message counts only if it comes from that exact frame
 * (an opaque origin, "null"), carries the frame's one-time nonce and the
 * version shown, has the expected shape and size, and stays within the rate.
 * The server checks the element path again when a comment is saved.
 */
export type BridgeMessage = { type: 'ready' } | { type: 'selection'; path: string | null; quote: string };

/** body>tag:n>tag:n…, as the Site page reports it (and the server accepts it). */
const ELEMENT_PATH = /^body(?:>[A-Za-z][A-Za-z0-9-]{0,40}:\d{1,4}){0,60}$/;
const MAX_QUOTE = 2000;
const MAX_PER_SECOND = 10;

export class BridgeGate {
  private stamps: number[] = [];

  constructor(private readonly options: { frame(): Window | null | undefined; nonce: string; revisionId: string; now?(): number }) {}

  read(event: Pick<MessageEvent, 'data' | 'source' | 'origin'>): BridgeMessage | null {
    const frame = this.options.frame();
    if (!frame || event.source !== frame || event.origin !== 'null') return null;
    const data = event.data as Record<string, unknown> | null;
    if (!data || typeof data !== 'object' || data.nonce !== this.options.nonce || data.revisionId !== this.options.revisionId) return null;
    if (!this.allow()) return null;
    if (data.type === 'mystiq-site:ready') return { type: 'ready' };
    if (data.type !== 'mystiq-site:selection') return null;
    if (data.path === null && data.quote === '') return { type: 'selection', path: null, quote: '' };
    if (typeof data.path !== 'string' || !ELEMENT_PATH.test(data.path)) return null;
    if (typeof data.quote !== 'string' || !data.quote.trim() || data.quote.length > MAX_QUOTE) return null;
    return { type: 'selection', path: data.path, quote: data.quote };
  }

  private allow(): boolean {
    const now = (this.options.now ?? Date.now)();
    this.stamps = this.stamps.filter((stamp) => now - stamp < 1000);
    if (this.stamps.length >= MAX_PER_SECOND) return false;
    this.stamps.push(now);
    return true;
  }
}

/** A one-time value for one Site frame (16 random bytes, URL-safe). */
export function newNonce(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
